import { ChevronDown, ChevronRight } from 'lucide-react'
import type { DataTableColumn } from '@/components/DataTable'
import { RowActions } from '@/components/RowActions'
import { Tag } from '@/components/Tag'
import { Tooltip } from '@/components/Tooltip'
import { lookupTypeLabel } from '@/lib/lookupTypes'
import type { LookupEntry } from '@/types/bundle'

export interface LookupTableRow {
  localKey: string
  entry: LookupEntry
}

export interface LookupTableColumnsOptions {
  expandedKey: string | null
  onToggleExpand: (localKey: string) => void
  onDuplicate: (row: LookupTableRow) => void
  onDelete: (row: LookupTableRow) => void
}

// Collapsed-row cell renderers, passed to DataTable's `columns` - the Name
// cell doubles as the row's expand/collapse toggle (PRD 05: "Row click
// expands inline"), same header-button affordance FieldRow uses.
export function getLookupTableColumns({
  expandedKey,
  onToggleExpand,
  onDuplicate,
  onDelete,
}: LookupTableColumnsOptions): DataTableColumn<LookupTableRow>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      render: (row) => {
        const expanded = row.localKey === expandedKey
        return (
          <button
            type="button"
            onClick={() => onToggleExpand(row.localKey)}
            className="flex items-center gap-2 text-left font-medium text-text-primary"
          >
            {expanded ? (
              <ChevronDown size={16} strokeWidth={1.5} className="text-text-secondary" />
            ) : (
              <ChevronRight size={16} strokeWidth={1.5} className="text-text-secondary" />
            )}
            {row.entry.name.trim() || 'Untitled entry'}
          </button>
        )
      },
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <Tag label={lookupTypeLabel(row.entry.type)} />,
    },
    {
      key: 'aliases',
      header: 'Aliases',
      render: (row) => {
        const aliases = row.entry.aliases ?? []
        if (aliases.length === 0) return <span className="text-text-secondary">-</span>
        return (
          <Tooltip text={aliases.join(', ')}>
            <span className="text-text-secondary">
              {aliases.length} alias{aliases.length === 1 ? '' : 'es'}
            </span>
          </Tooltip>
        )
      },
    },
    {
      key: 'domain',
      header: 'Domain',
      render: (row) => row.entry.domain?.trim() || <span className="text-text-secondary">-</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <RowActions onDuplicate={() => onDuplicate(row)} onDelete={() => onDelete(row)} />
      ),
    },
  ]
}
